import { CartesianGrid, Legend, ReferenceArea, ReferenceLine, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from "recharts";
import { WeatherIcon } from "@/components/WeatherIcon";
import { useT } from "@/lib/i18n";
import { formatDate } from "@/lib/format";

export const TYRE_COLORS = { fl: "#007AFF", fr: "#10B981", rl: "#FFB800", rr: "#FF3B30" };

const PointTooltip = ({ active, payload, unit, xUnit }) => {
  const { t, lang } = useT();
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div data-testid="pressure-tooltip" className="bg-[#090A0F] border border-[#282D3D] rounded-sm p-3 font-mono text-xs space-y-1 shadow-xl">
      <div className="font-heading uppercase font-bold text-base text-white">{p.session_name}</div>
      <div className="text-slate-400">{formatDate(p.event_date, lang)} · {p.event_name}</div>
      {(p.driver || p.category) && <div className="text-slate-300">{[p.driver, p.category].filter(Boolean).join(" · ")}</div>}
      <div className="flex items-center gap-1.5 text-[#FFB800]">
        <WeatherIcon weather={p.weather} className="w-3.5 h-3.5" />
        {p.weather ? t(`weather_${p.weather}`) : ""}{p.air_temp != null ? ` · ${t("air_short")} ${p.air_temp}°C` : ""}{p.track_temp != null ? ` · ${t("track_short")} ${p.track_temp}°C` : ""}
      </div>
      {(p.humidity != null || p.wind_speed != null) && (
        <div className="text-[#60A5FA]">{[p.humidity != null ? `${t("hum_short")} ${p.humidity}%` : "", p.wind_speed != null ? `${t("wind_short")} ${p.wind_speed} km/h` : ""].filter(Boolean).join(" · ")}</div>
      )}
      <div className="pt-1 border-t border-[#282D3D]">
        <span style={{ color: TYRE_COLORS[p.tyre] }}>{t(`tyre_${p.tyre}`)}</span>: <b className="text-white">{p.y} {unit}</b> @ {p.x}{xUnit}
      </div>
    </div>
  );
};

export const PressureChart = ({ series, unit, trend, yLabel, band, testid = "pressure-chart", title, xAxis }) => {
  const { t } = useT();
  const xl = xAxis?.label || t("track_temp");
  const xu = xAxis ? (xAxis.unit === "km/h" ? " km/h" : xAxis.unit) : "°C";
  const tick = { fill: "#94A3B8", fontSize: 11, fontFamily: "JetBrains Mono" };
  const yl = yLabel || t("pressure_hot");
  return (
    <div data-testid={testid} className="bg-[#12141C] border border-[#282D3D] rounded-sm p-3 sm:p-5 h-[420px] flex flex-col">
      {title && <div className="font-heading uppercase font-bold text-lg mb-1 flex items-center gap-2"><span className="w-1 h-4 bg-[#FFB800]" />{title}</div>}
      <div className="flex-1 min-h-0">
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 10, right: 30, bottom: 30, left: 10 }}>
          <CartesianGrid stroke="#282D3D" strokeDasharray="3 3" />
          <XAxis type="number" dataKey="x" name={xl} unit={xu} domain={[(min) => (xAxis && xAxis.unit !== "°C" ? Math.max(0, Math.floor(min - 2)) : Math.floor(min - 2)), (max) => Math.ceil(max + 2)]} allowDecimals={false} tick={tick} stroke="#282D3D"
            label={{ value: xl, position: "insideBottom", offset: -18, fill: "#64748B", fontSize: 11 }} />
          <YAxis type="number" dataKey="y" name={yl} domain={band ? [(min) => Math.min(min, band.lo) - 0.05, (max) => Math.max(max, band.hi) + 0.05] : ["auto", "auto"]} tick={tick} stroke="#282D3D" width={56}
            tickFormatter={(v) => Number(v).toFixed(unit === "psi" ? 1 : 2)}
            label={{ value: `${yl} (${unit})`, angle: -90, position: "insideLeft", fill: "#64748B", fontSize: 11, dy: 60 }} />
          {band && <ReferenceArea y1={band.lo} y2={band.hi} fill="#10B981" fillOpacity={0.12} stroke="#10B981" strokeOpacity={0.3} ifOverflow="extendDomain" />}
          {band && <ReferenceLine y={band.mid} stroke="#10B981" strokeDasharray="6 4" label={{ value: `${t("tyre_target")} ${band.mid}`, fill: "#10B981", fontSize: 11, position: "insideTopRight" }} />}
          <Tooltip content={<PointTooltip unit={unit} xUnit={xu} />} cursor={{ stroke: "#475569", strokeDasharray: "3 3" }} />
          <Legend verticalAlign="top" height={30} wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 11 }} />
          {series.map((s) => (
            <Scatter key={s.tyre} name={t(`tyre_${s.tyre}`)} data={s.points} fill={TYRE_COLORS[s.tyre]}
              line={trend && s.points.length > 1 ? { stroke: TYRE_COLORS[s.tyre], strokeWidth: 1.5, strokeDasharray: "5 4" } : false}
              lineType="fitting" isAnimationActive={false} />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
};
